import {
  apiErrorSchema,
  cleanupSessionResponseSchema,
  completeUploadResponseSchema,
  createJobResponseSchema,
  createSessionResponseSchema,
  createUploadSessionResponseSchema,
  deleteAssetResponseSchema,
  downloadSessionResponseSchema,
  getJobResponseSchema,
  heartbeatResponseSchema,
  type ApiErrorResponse,
  type CompleteUploadResponse,
  type CreateJobRequest,
  type CreateJobResponse,
  type CreateUploadSessionRequest,
  type CreateUploadSessionResponse,
  type DownloadSessionResponse,
  type GetJobResponse,
  type HeartbeatResponse,
} from "@wendo/contracts";
import type { z } from "zod";

import { sessionStore, type SessionStore } from "./session-store";

export class ApiClientError extends Error {
  readonly code: ApiErrorResponse["error"]["code"];
  readonly requestId: string | null;

  constructor(error: ApiErrorResponse["error"]) {
    super(error.message);
    this.name = "ApiClientError";
    this.code = error.code;
    this.requestId = error.requestId;
  }
}

export interface DocumentApi {
  createUploadSession(
    request: CreateUploadSessionRequest,
  ): Promise<CreateUploadSessionResponse>;
  completeUpload(assetId: string): Promise<CompleteUploadResponse>;
  createJob(
    request: CreateJobRequest,
    idempotencyKey: string,
  ): Promise<CreateJobResponse>;
  getJob(jobId: string): Promise<GetJobResponse>;
  createDownloadSession(assetId: string): Promise<DownloadSessionResponse>;
  deleteAsset(assetId: string): Promise<void>;
  heartbeat(): Promise<HeartbeatResponse>;
  cleanup(): Promise<void>;
  cleanupKeepalive(): void;
}

type Fetcher = typeof fetch;

const browserFetch: Fetcher = (input, init) => globalThis.fetch(input, init);

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export class HttpDocumentApi implements DocumentApi {
  private sessionCreation: Promise<string> | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly store: SessionStore,
    private readonly fetcher: Fetcher = browserFetch,
  ) {}

  async createUploadSession(
    request: CreateUploadSessionRequest,
  ): Promise<CreateUploadSessionResponse> {
    return this.protectedRequest(
      "/v1/assets/upload-session",
      createUploadSessionResponseSchema,
      { method: "POST", body: JSON.stringify(request) },
    );
  }

  async completeUpload(assetId: string): Promise<CompleteUploadResponse> {
    return this.protectedRequest(
      `/v1/assets/${encodeURIComponent(assetId)}/complete`,
      completeUploadResponseSchema,
      { method: "POST" },
    );
  }

  async createJob(
    request: CreateJobRequest,
    idempotencyKey: string,
  ): Promise<CreateJobResponse> {
    return this.protectedRequest("/v1/jobs", createJobResponseSchema, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(request),
    });
  }

  async getJob(jobId: string): Promise<GetJobResponse> {
    return this.protectedRequest(
      `/v1/jobs/${encodeURIComponent(jobId)}`,
      getJobResponseSchema,
      { method: "GET" },
    );
  }

  async createDownloadSession(
    assetId: string,
  ): Promise<DownloadSessionResponse> {
    return this.protectedRequest(
      `/v1/assets/${encodeURIComponent(assetId)}/download-session`,
      downloadSessionResponseSchema,
      { method: "POST" },
    );
  }

  async deleteAsset(assetId: string): Promise<void> {
    await this.protectedRequest(
      `/v1/assets/${encodeURIComponent(assetId)}`,
      deleteAssetResponseSchema,
      { method: "DELETE" },
    );
  }

  async heartbeat(): Promise<HeartbeatResponse> {
    return this.protectedRequest(
      "/v1/sessions/heartbeat",
      heartbeatResponseSchema,
      { method: "POST" },
    );
  }

  async cleanup(): Promise<void> {
    if (this.store.get() === null) return;
    try {
      await this.protectedRequest(
        "/v1/sessions/current",
        cleanupSessionResponseSchema,
        { method: "DELETE" },
      );
    } finally {
      this.store.clear();
    }
  }

  cleanupKeepalive(): void {
    const token = this.store.get();
    if (token === null) return;
    this.store.clear();
    void this.fetcher(`${this.baseUrl}/v1/sessions/current`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {
      // Browser shutdown can cancel this request; the server-side inactivity sweep is the fallback.
    });
  }

  private async protectedRequest<T>(
    path: string,
    schema: z.ZodType<T>,
    init: RequestInit,
  ): Promise<T> {
    const token = await this.ensureSession();
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (init.body !== undefined && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      headers,
    });
    const body = await parseJson(response);
    if (!response.ok) {
      const parsedError = apiErrorSchema.safeParse(body);
      if (parsedError.success) throw new ApiClientError(parsedError.data.error);
      throw new ApiClientError({
        code: "INTERNAL_ERROR",
        message: "服务返回了无法识别的错误",
        requestId: response.headers.get("X-Request-ID") ?? "unknown",
      });
    }
    return schema.parse(body);
  }

  private async ensureSession(): Promise<string> {
    const existing = this.store.get();
    if (existing !== null) return existing;
    this.sessionCreation ??= this.createSession();
    try {
      return await this.sessionCreation;
    } finally {
      this.sessionCreation = null;
    }
  }

  private async createSession(): Promise<string> {
    const response = await this.fetcher(`${this.baseUrl}/v1/sessions`, {
      method: "POST",
    });
    const body = await parseJson(response);
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(body);
      if (parsed.success) throw new ApiClientError(parsed.data.error);
      throw new ApiClientError({
        code: "INTERNAL_ERROR",
        message: "无法创建临时会话",
        requestId: response.headers.get("X-Request-ID") ?? "unknown",
      });
    }
    const session = createSessionResponseSchema.parse(body);
    this.store.set(session.sessionToken);
    return session.sessionToken;
  }
}

const configuredBaseUrl = (
  import.meta.env.VITE_API_BASE_URL as string | undefined
)?.replace(/\/$/u, "");

export const documentApi = new HttpDocumentApi(
  configuredBaseUrl ?? "http://127.0.0.1:8787",
  sessionStore,
);
